'use client';

/**
 * FACTORY ONE-FOCUS DECK — builds are the cards (/api/factory/status).
 * Focus: WHY = profile/mode/status + build log tail, PREVIEW = the 7-phase
 * pipeline rail with gate details, DECIDE = ▶ RESUME (POST /api/factory/resume).
 */
import { useCallback, useEffect, useState } from 'react';
import { OneFocusDeck, bigVerb, CY, type OFItem, type OFAdapter } from './OneFocus';
import { mono, display } from '@/future/primitives';

const PH_COLOR: Record<string, string> = { done: '#5df2b4', running: '#9ef0ff', pending: '#56788c', failed: '#ff7d8a' };

function Why({ item }: { item: OFItem }) {
  const b = item.raw;
  const doneN = (b.phases || []).filter((p: any) => p.status === 'done').length;
  return (
    <>
      {[['DOMAIN', b.domain], ['PROFILE', b.profile], ['MODE', b.mode], ['STATUS', b.status?.toUpperCase()],
        ['PROGRESS', `${doneN}/${(b.phases || []).length} PHASES`],
        ['ORCHESTRATOR', b.orchestratorAlive ? 'ALIVE' : 'STOPPED'],
        ['CREATED', b.created_at ? new Date(b.created_at).toLocaleString() : '—']].map(([k, v]) => (
        <div key={k as string} className="flex justify-between" style={{ padding: '4px 0' }}>
          <span style={{ ...mono(15, '.16em'), color: '#7fa9bd' }}>{k}</span>
          <span style={{ ...mono(16, '.06em'), color: '#d7f2ff' }}>{v || '—'}</span>
        </div>
      ))}
      {b.buildLog && (
        <div style={{ marginTop: 14, borderTop: '1px solid rgba(125,232,255,.12)', paddingTop: 10 }}>
          <div style={{ ...mono(14, '.26em'), color: '#49677a', marginBottom: 6 }}>BUILD LOG · TAIL</div>
          <div style={{ ...mono(15, '.02em'), color: '#9fb8c6', lineHeight: 1.6, whiteSpace: 'pre-wrap', maxHeight: 200, overflow: 'auto' }}>
            {String(b.buildLog).split('\n').slice(-12).join('\n')}
          </div>
        </div>
      )}
    </>
  );
}

function Preview({ item }: { item: OFItem }) {
  const phases = item.raw.phases || [];
  return (
    <>
      <div style={{ ...mono(14, '.26em'), color: '#49677a', marginBottom: 12 }}>PHASE PIPELINE · BLUEPRINT → SHIP</div>
      {phases.map((p: any, i: number) => (
        <div key={p.id ?? i} className="flex items-start" style={{ gap: 12, marginBottom: 4 }}>
          <div className="flex flex-col items-center" style={{ width: 16 }}>
            <span style={{ width: 10, height: 10, borderRadius: '50%', background: PH_COLOR[p.status] || '#56788c', boxShadow: p.status === 'running' ? '0 0 12px #9ef0ff' : p.status === 'done' ? '0 0 8px #5df2b4' : 'none', flexShrink: 0, marginTop: 4 }} />
            {i < phases.length - 1 && <span style={{ width: 1, flex: 1, minHeight: 22, background: p.status === 'done' ? 'rgba(93,242,180,.4)' : 'rgba(125,232,255,.12)' }} />}
          </div>
          <div style={{ paddingBottom: 12, flex: 1 }}>
            <div className="flex items-center" style={{ gap: 10 }}>
              <span style={{ ...display(25, 400), color: p.status === 'pending' ? '#56788c' : '#e9f9ff' }}>{(p.name || '').toUpperCase()}</span>
              <span style={{ ...mono(13, '.18em'), color: PH_COLOR[p.status] || '#56788c' }}>{(p.status || '').toUpperCase()}</span>
              {p.completedAt && <span style={{ ...mono(13, '.1em'), color: '#3d4654' }}>{new Date(p.completedAt).toLocaleString()}</span>}
            </div>
            {p.details && <div style={{ ...display(21, 300), color: '#9fb8c6', marginTop: 2 }}>{p.details}</div>}
          </div>
        </div>
      ))}
    </>
  );
}

function Decide({ item, refresh, speak }: { item: OFItem; refresh: () => void; speak: (t: string) => void }) {
  const b = item.raw;
  const [busy, setBusy] = useState(false);
  const resumable = b.status !== 'running' || !b.orchestratorAlive;
  const resume = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await fetch('/api/factory/resume', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ slug: b.slug }) });
      refresh(); speak(`Factory resumed — ${b.domain} is building again.`);
    } finally { setBusy(false); }
  };
  return (
    <>
      {resumable ? (
        <div onMouseUp={resume} style={{ ...bigVerb(), opacity: busy ? .6 : 1, textAlign: 'center' }}>{busy ? 'RESUMING…' : '▶ RESUME BUILD'}</div>
      ) : (
        <div style={{ padding: '14px 0', textAlign: 'center', borderRadius: 12, border: '1px solid rgba(158,240,255,.4)', color: '#9ef0ff', ...mono(18, '.26em') }}>RUNNING · ORCHESTRATOR ALIVE</div>
      )}
      <a href={`https://${b.domain}`} target="_blank" rel="noreferrer" style={{ textDecoration: 'none' }}>
        <div style={{ width: '100%', padding: '10px 0', marginTop: 10, borderRadius: 12, background: 'transparent', border: '1px solid rgba(125,232,255,.3)', color: CY, ...mono(16, '.26em'), textAlign: 'center', cursor: 'pointer' }}>
          OPEN {b.domain?.toUpperCase()} ↗
        </div>
      </a>
      <div style={{ ...mono(13, '.12em'), color: '#3d4654', marginTop: 10, textAlign: 'center' }}>POST /api/factory/resume</div>
    </>
  );
}

export function FactoryDeckSurface() {
  const [items, setItems] = useState<OFItem[]>([]);
  const [loaded, setLoaded] = useState(false);
  const load = useCallback(() => {
    fetch('/api/factory/status').then(r => r.json()).then(d => {
      const builds: any[] = d.builds || [];
      setItems(builds.map((b: any): OFItem => {
        const doneN = (b.phases || []).filter((p: any) => p.status === 'done').length;
        const cur = (b.phases || []).find((p: any) => p.status === 'running') || (b.phases || []).find((p: any) => p.status === 'pending');
        return {
          id: b.slug, title: b.domain || b.slug, type: `FACTORY · PROFILE ${b.profile || '—'}`,
          status: b.status,
          kpis: [
            { l: 'PHASES', v: `${doneN}/${(b.phases || []).length}` },
            { l: 'CURRENT', v: (cur?.name || 'DONE').toUpperCase() },
            { l: 'ORCH', v: b.orchestratorAlive ? 'ALIVE' : 'OFF' },
          ],
          foot: b.slug, raw: b,
        };
      }));
      setLoaded(true);
    }).catch(() => setLoaded(true));
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 30_000); return () => clearInterval(t); }, [load]);

  const adapter: OFAdapter = {
    kind: 'factory',
    strap: (its) => `ONE FOCUS · ${its.length} BUILDS · BLUEPRINT → SHIP`,
    decidable: (it) => it.raw.status !== 'running' || !it.raw.orchestratorAlive,
    Why, Preview, Decide,
  };
  return <OneFocusDeck items={items} loaded={loaded} adapter={adapter} refresh={load} />;
}
